import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, buildWhatsAppLink, formatINR } from "@/lib/api";
import PackageCard from "@/components/PackageCard";
import BookingInquiryDialog from "@/components/BookingInquiryDialog";
import { ArrowRight, ShieldCheck, Users, Compass, Waves, Heart, Star, Percent, Clock, MapPin, MessageCircle, Hotel, Car, Ship } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1638459032642-c658d3d888c0?fm=jpg&q=80&w=2400&auto=format&fit=crop";

const DESTINATIONS = [
  {
    name: "Havelock Island",
    img: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d2/RadhaNagar_Beach%2C_Havelock_Island%2C_Andaman_%26_Nicobar_Islands.jpg/1280px-RadhaNagar_Beach%2C_Havelock_Island%2C_Andaman_%26_Nicobar_Islands.jpg",
    tag: "Radhanagar Beach",
    desc: "Powder-soft sand, warm shallows, and Asia's most beautiful sunset.",
  },
  {
    name: "Neil Island",
    img: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/Natural_Bridge%2C_Neil_Island%2C_Andaman%2C_India.JPG/1280px-Natural_Bridge%2C_Neil_Island%2C_Andaman%2C_India.JPG",
    tag: "Natural Bridge",
    desc: "Slow pace, cycled roads, and reefs a few metres from the shore.",
  },
  {
    name: "Port Blair",
    img: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fe/Front_View_of_Cellular_Jail%2C_Port_Blair.JPG/1280px-Front_View_of_Cellular_Jail%2C_Port_Blair.JPG",
    tag: "Cellular Jail",
    desc: "Gateway to the islands with heritage, museums and the light & sound show.",
  },
  {
    name: "Baratang",
    img: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/99/Baratang_-_Walking_bridge_through_mangrove_forest.jpg/1280px-Baratang_-_Walking_bridge_through_mangrove_forest.jpg",
    tag: "Limestone Caves",
    desc: "Mangrove creeks, mud volcanoes and one of the most raw jungle rides.",
  },
];

const WHY = [
  { icon: ShieldCheck, title: "Directly with locals", desc: "No middlemen. You pay the real price, we keep the trip honest." },
  { icon: Compass, title: "Handcrafted itineraries", desc: "Every plan is tuned to your pace, group and budget — not sold off a shelf." },
  { icon: Users, title: "On-ground team", desc: "Our team lives on the islands. Real support before, during and after." },
  { icon: Waves, title: "Sea safety first", desc: "Only certified operators for scuba, sea walk and cruises. No shortcuts." },
];

const ACTIVITIES = [
  {
    key: "scuba",
    name: "Scuba Diving",
    where: "Havelock · Elephant Beach / Nemo Reef",
    price: 3500,
    mrp: 5000,
    unit: "per dive",
    duration: "45 min",
    img: "https://images.unsplash.com/photo-1544551763-8dd44758c2dd?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    desc: "Certified PADI-guided beginner dive to 12m. No experience required.",
  },
  {
    key: "sea-walk",
    name: "Sea Walking",
    where: "North Bay · Port Blair",
    price: 2800,
    mrp: 3800,
    unit: "per person",
    duration: "20 min",
    img: "https://images.unsplash.com/photo-1534081333815-ae5019106622?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    desc: "Walk on the ocean floor with a helmet camera — safe for non-swimmers.",
  },
  {
    key: "snorkelling",
    name: "Snorkelling",
    where: "Elephant Beach · Havelock",
    price: 1200,
    mrp: 1800,
    unit: "per person",
    duration: "1 hour",
    img: "https://images.unsplash.com/photo-1682687982502-1529b3b33f85?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    desc: "Shallow-reef snorkelling with instructor, mask and fins provided.",
  },
  {
    key: "jet-ski",
    name: "Jet Ski Ride",
    where: "Corbyn's Cove · Port Blair",
    price: 800,
    mrp: 1200,
    unit: "per ride",
    duration: "10 min",
    img: "https://images.unsplash.com/photo-1554132267-d06483b00adc?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    desc: "Solo or double-seater with certified rider — pure adrenaline.",
  },
  {
    key: "parasailing",
    name: "Parasailing",
    where: "Havelock · Kalapathar",
    price: 3200,
    mrp: 4500,
    unit: "per flight",
    duration: "8 min",
    img: "https://images.unsplash.com/photo-1542319281-2a3772c20dfc?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    desc: "Rise 100 ft above turquoise water — the Andaman shot for your feed.",
  },
  {
    key: "banana",
    name: "Banana Boat",
    where: "Multiple beaches",
    price: 600,
    mrp: 900,
    unit: "per person",
    duration: "15 min",
    img: "https://images.unsplash.com/photo-1723074295323-66dee3315091?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    desc: "Group-friendly water ride — 4 to 6 riders per boat.",
  },
  {
    key: "kayak",
    name: "Mangrove Kayaking",
    where: "Havelock · Chidiya Tapu",
    price: 1800,
    mrp: 2500,
    unit: "per person",
    duration: "1.5 hours",
    img: "https://images.unsplash.com/photo-1558281050-4c33200099c7?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    desc: "Guided kayak through bioluminescent mangroves (season permitting).",
  },
  {
    key: "glass-boat",
    name: "Glass Bottom Boat",
    where: "North Bay · Port Blair",
    price: 1000,
    mrp: 1500,
    unit: "per person",
    duration: "30 min",
    img: "https://images.unsplash.com/photo-1682687981630-cefe9cd73072?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    desc: "See living coral and reef fish without getting wet — great for kids & elders.",
  },
];

const TESTIMONIALS = [
  { name: "Ananya & Rohit", trip: "Honeymoon, 6D", text: "Every small detail — the beach dinner, the room upgrade, the ferry timing — felt personal. Best decision to book direct." },
  { name: "The Menon Family", trip: "Family, 5D", text: "Kids loved the glass boat and Elephant Beach. Guides were patient and everything ran on time. Highly recommend." },
  { name: "Karan V.", trip: "Adventure, 7D", text: "Two dives at Havelock, sea walk, kayaking — genuinely well planned. Prices were transparent from day one." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api
      .get("/packages", { params: { featured: true } })
      .then((r) => setFeatured(r.data || []))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[88vh] flex items-end"
      >
        <img
          src={HERO_IMG}
          alt="Andaman Islands"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative container-narrow py-16 md:py-24 text-white w-full">
          <span className="chip chip-dark" data-testid="hero-eyebrow">Andaman &amp; Nicobar · India</span>
          <h1 className="font-heading font-light text-5xl sm:text-6xl md:text-7xl leading-[1.05] mt-6 max-w-3xl">
            The islands, curated.
            <br />
            The way you want them.
          </h1>
          <p className="mt-6 max-w-xl text-white/85 text-base sm:text-lg font-body">
            Sold directly by an Andaman-based team. Honeymoons, family
            holidays, scuba weeks and quick escapes — designed the slow, careful
            way.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/packages"
              className="btn-primary"
              data-testid="hero-explore-btn"
            >
              Explore Packages <ArrowRight className="w-4 h-4" />
            </Link>
            <BookingInquiryDialog
              trigger={
                <button className="btn-ghost-light" data-testid="hero-plan-btn">
                  Plan a custom trip
                </button>
              }
            />
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="container-narrow py-24">
        <div className="grid md:grid-cols-3 gap-12 items-start">
          <div className="md:col-span-1">
            <div className="overline">Why book direct</div>
            <h2 className="font-heading text-4xl sm:text-5xl leading-tight mt-3">
              A quieter, better way to see the Andamans.
            </h2>
            <p className="text-[color:var(--brand-text-soft)] mt-4 max-w-md">
              We're a small, on-ground team of guides, drivers and boat
              operators. No agency layers. Just people who love these islands.
            </p>
          </div>
          <div className="md:col-span-2 grid sm:grid-cols-2 gap-8">
            {WHY.map((w) => (
              <div key={w.title} data-testid={`why-${w.title.toLowerCase().replace(/\s+/g, "-")}`} className="flex gap-4">
                <div className="p-3 bg-[color:var(--brand-surface-alt)] rounded-full h-max">
                  <w.icon className="w-5 h-5 text-[color:var(--brand-primary)]" strokeWidth={1.6} />
                </div>
                <div>
                  <h3 className="font-heading text-xl">{w.title}</h3>
                  <p className="text-sm text-[color:var(--brand-text-soft)] mt-1 leading-relaxed">
                    {w.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NAUTIKA FERRY PARTNER CALLOUT */}
      <section
        className="bg-[color:var(--brand-surface-alt)] py-16"
        data-testid="ferry-partner-section"
      >
        <div className="container-narrow grid md:grid-cols-2 gap-10 items-center">
          <div className="img-frame aspect-[16/10] order-2 md:order-1">
            <img
              src="https://gonautika.com/assets/19x/landingPage/still2.webp"
              alt="Nautika catamaran ferry between Andaman islands"
              loading="lazy"
              data-testid="nautika-ferry-image"
            />
          </div>
          <div className="order-1 md:order-2">
            <div className="overline">Preferred ferry partner</div>
            <h2 className="font-heading text-4xl sm:text-5xl mt-3 leading-tight">
              We sail with <span className="italic">Nautika</span>.
            </h2>
            <p className="mt-4 text-[color:var(--brand-text-soft)] leading-relaxed">
              Andaman's fastest private catamaran fleet with 400+ Luxury,
              Royal &amp; Business seats, AC cabins and a smooth 24-knot ride.
              We use Nautika as our default inter-island transfer for every
              package — Port Blair ⇄ Havelock ⇄ Neil.
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <li className="flex gap-2">
                <span className="text-[color:var(--brand-primary)]">✓</span>
                90-min crossings
              </li>
              <li className="flex gap-2">
                <span className="text-[color:var(--brand-primary)]">✓</span>
                Fully air-conditioned
              </li>
              <li className="flex gap-2">
                <span className="text-[color:var(--brand-primary)]">✓</span>
                On-time departures
              </li>
              <li className="flex gap-2">
                <span className="text-[color:var(--brand-primary)]">✓</span>
                Confirmed seats
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* STANDALONE SERVICES — hotel + vehicle + ferry */}
      <section className="container-narrow py-24" data-testid="services-section">
        <div className="max-w-2xl">
          <div className="overline">Beyond packages</div>
          <h2 className="font-heading text-4xl sm:text-5xl mt-2 leading-tight">
            Prefer to book pieces? We do those too.
          </h2>
          <p className="mt-3 text-sm text-[color:var(--brand-text-soft)] max-w-xl">
            Already flying in on your own? Talk to us about individual services —
            we'll get you the same insider rates without the full package.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {[
            {
              icon: Hotel,
              key: "hotel",
              title: "Hotel Booking",
              body: "Curated stays across Port Blair, Havelock and Neil — from beach hostels to sea-view villas. Best-rate guaranteed vs OTA listings.",
              cta: "Get hotel quote",
              wa: "Hi! I'd like a hotel booking quote for the Andamans.",
            },
            {
              icon: Car,
              key: "vehicle",
              title: "Vehicle Booking",
              body: "Airport pickups, private cabs, self-drive scooters and Innova/Tempo Traveller for groups. All local, licensed drivers.",
              cta: "Get vehicle quote",
              wa: "Hi! I need a vehicle booking (cab / scooter) in the Andamans.",
            },
            {
              icon: Ship,
              key: "ferry",
              title: "Ferry Ticketing",
              body: "Nautika, Makruzz and government ferry tickets — Business, Royal or Premium class. Confirmed seats, no walk-in queues.",
              cta: "Book ferry tickets",
              wa: "Hi! I need help booking ferry tickets between the Andaman islands.",
            },
          ].map((s) => (
            <div
              key={s.key}
              className="card-flat p-6 flex flex-col"
              data-testid={`service-${s.key}`}
            >
              <div className="p-3 bg-[color:var(--brand-surface-alt)] rounded-full w-max">
                <s.icon
                  className="w-5 h-5 text-[color:var(--brand-primary)]"
                  strokeWidth={1.6}
                />
              </div>
              <h3 className="font-heading text-2xl mt-4">{s.title}</h3>
              <p className="text-sm text-[color:var(--brand-text-soft)] mt-2 leading-relaxed flex-1">
                {s.body}
              </p>
              <div className="mt-5 flex gap-3">
                <BookingInquiryDialog
                  pkg={{ id: null, title: `${s.title} — Standalone` }}
                  trigger={
                    <button
                      className="text-sm font-medium text-[color:var(--brand-primary)] hover:underline"
                      data-testid={`service-${s.key}-inquiry`}
                    >
                      {s.cta} →
                    </button>
                  }
                />
                <a
                  href={buildWhatsAppLink(s.wa)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[color:var(--brand-text-soft)] hover:text-[color:var(--brand-primary)]"
                  data-testid={`service-${s.key}-whatsapp`}
                >
                  WhatsApp
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED PACKAGES */}
      <section className="container-narrow py-16" data-testid="featured-packages-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="overline">Featured</div>
            <h2 className="font-heading text-4xl sm:text-5xl mt-2">Signature journeys</h2>
          </div>
          <Link to="/packages" className="link-underline" data-testid="see-all-packages">
            See all packages <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p) => (
            <PackageCard key={p.id} pkg={p} />
          ))}
        </div>
      </section>

      {/* DESTINATIONS */}
      <section className="bg-[color:var(--brand-surface-alt)] py-24 mt-16" data-testid="destinations-section">
        <div className="container-narrow">
          <div className="max-w-2xl">
            <div className="overline">Where you'll go</div>
            <h2 className="font-heading text-4xl sm:text-5xl mt-2">The islands, one by one.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {DESTINATIONS.map((d) => (
              <div key={d.name} className="card-flat" data-testid={`destination-${d.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="img-frame aspect-[3/4]">
                  <img src={d.img} alt={d.name} loading="lazy" />
                </div>
                <div className="p-5">
                  <span className="overline">{d.tag}</span>
                  <h3 className="font-heading text-2xl mt-1">{d.name}</h3>
                  <p className="text-sm text-[color:var(--brand-text-soft)] mt-2">{d.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WATERSPORTS & ACTIVITIES — discounted add-ons */}
      <section className="container-narrow py-24" data-testid="activities-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
          <div>
            <div className="overline flex items-center gap-2">
              <Percent className="w-3.5 h-3.5" /> Book direct · save up to 30%
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl mt-2 leading-tight max-w-2xl">
              Water sports &amp; island activities, at insider prices.
            </h2>
          </div>
          <p className="text-sm text-[color:var(--brand-text-soft)] max-w-md">
            Add any of these to your package — pre-book with us and pay
            up to 30% less than counter rates on the beach. Certified
            operators, safety gear included.
          </p>
        </div>
        <div
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10"
          data-testid="activities-grid"
        >
          {ACTIVITIES.map((a) => {
            const savings = a.mrp - a.price;
            const pct = Math.round((savings / a.mrp) * 100);
            return (
              <div
                key={a.key}
                className="card-flat overflow-hidden flex flex-col"
                data-testid={`activity-${a.key}`}
              >
                <div className="img-frame aspect-[4/3] relative">
                  <img src={a.img} alt={a.name} loading="lazy" />
                  <span
                    className="absolute top-3 left-3 chip chip-dark"
                    data-testid={`activity-${a.key}-discount`}
                  >
                    −{pct}% off
                  </span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-heading text-xl leading-snug">{a.name}</h3>
                  <p className="text-xs text-[color:var(--brand-text-soft)] flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" /> {a.where}
                  </p>
                  <p className="text-sm mt-3 text-[color:var(--brand-text)] leading-relaxed">
                    {a.desc}
                  </p>
                  <div className="rule my-4" />
                  <div className="flex items-end justify-between mt-auto">
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-heading text-xl">
                          {formatINR(a.price)}
                        </span>
                        <span className="text-xs line-through text-[color:var(--brand-text-soft)]">
                          {formatINR(a.mrp)}
                        </span>
                      </div>
                      <span className="text-[11px] text-[color:var(--brand-text-soft)]">
                        {a.unit} ·{" "}
                        <Clock className="w-3 h-3 inline -mt-0.5" /> {a.duration}
                      </span>
                    </div>
                    <BookingInquiryDialog
                      pkg={{ id: null, title: `${a.name} — Water sports add-on` }}
                      trigger={
                        <button
                          className="text-xs font-medium text-[color:var(--brand-primary)] hover:underline"
                          data-testid={`activity-${a.key}-book`}
                        >
                          Book
                        </button>
                      }
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* GOOD TO KNOW — quick facts */}
      <section
        className="bg-[color:var(--brand-surface-alt)] py-24"
        data-testid="good-to-know-section"
      >
        <div className="container-narrow">
          <div className="max-w-2xl">
            <div className="overline">Good to know</div>
            <h2 className="font-heading text-4xl sm:text-5xl mt-2">
              A few things before you fly.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {[
              {
                title: "Best time to visit",
                body: "October to mid-May. Clear seas, ferries running, best diving visibility from Feb to April.",
              },
              {
                title: "Getting there",
                body: "Fly to Veer Savarkar Airport (IXZ), Port Blair. Direct flights from Chennai, Kolkata, Delhi, Bengaluru.",
              },
              {
                title: "Permits & ID",
                body: "Indian nationals: only photo ID. Foreign visitors: a free RAP granted on arrival, valid for 30 days.",
              },
              {
                title: "Payments",
                body: "We accept UPI and bank transfer only — no card payments. Reservations confirmed with a 25% advance, balance on arrival.",
              },
            ].map((f) => (
              <div key={f.title} data-testid={`gtk-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-10 h-px bg-[color:var(--brand-primary)] mb-4" />
                <h3 className="font-heading text-2xl leading-tight">{f.title}</h3>
                <p className="text-sm text-[color:var(--brand-text-soft)] mt-2 leading-relaxed">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="container-narrow py-24" data-testid="testimonials-section">
        <div className="max-w-2xl">
          <div className="overline">Sample stories · illustrative</div>
          <h2 className="font-heading text-4xl sm:text-5xl mt-2">
            The kind of trips we love to build.
          </h2>
          <p className="mt-3 text-sm text-[color:var(--brand-text-soft)] max-w-xl">
            Verified Google reviews will appear here once our Google Business
            Profile goes live. For now, these are representative examples of the
            journeys we craft.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="card-flat p-6">
              <div className="flex gap-1 text-[color:var(--brand-accent)]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" strokeWidth={0} />
                ))}
              </div>
              <p className="font-heading text-xl mt-4 leading-snug">"{t.text}"</p>
              <div className="mt-6 flex items-center justify-between text-sm">
                <span className="font-medium">{t.name}</span>
                <span className="overline">{t.trip}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-narrow py-16">
        <div className="border border-[color:var(--brand-border)] p-10 md:p-16 grid md:grid-cols-2 gap-10 items-center bg-[color:var(--brand-surface)]">
          <div>
            <div className="overline">Ready when you are</div>
            <h2 className="font-heading text-4xl sm:text-5xl mt-3 leading-tight">
              Tell us your dates.
              <br />
              We'll do the rest.
            </h2>
            <p className="mt-4 text-[color:var(--brand-text-soft)] max-w-md">
              Prefer a call? Message us on WhatsApp and get a custom quote within
              a few hours.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <BookingInquiryDialog
              trigger={
                <button className="btn-primary" data-testid="cta-inquiry-btn">
                  <Heart className="w-4 h-4" /> Send an inquiry
                </button>
              }
            />
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline"
              data-testid="cta-whatsapp-btn"
            >
              WhatsApp us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
